/**
 * The "// includes ..." subtitle shown in the placement HUD (PlacementHud),
 * centralised so wording + amounts can't drift between the two callers
 * (ReinforceUI, PostElimPlacementUI) and can be unit-tested.
 *
 * Breakdown by source (both shown when both are present):
 *   - **Elimination bonus** (post-elim-placement only): `config.eliminatePlayerBonus`.
 *   - **Card turn-in bonus**: the remaining `bonusArmies` beyond the elimination
 *     bonus (post-elim), or the whole `bonusArmies` in the reinforce phase (where
 *     the only bonus source is a card turn-in).
 *
 * Examples:
 *   post-elim, elim 5 + card 8 → "+5 elimination bonus, +8 card bonus"
 *   post-elim, elim 5 only     → "+5 elimination bonus"
 *   reinforce, card 8          → "+8 card bonus"
 *   no bonus                   → null (subtitle hidden)
 *
 * Note: the engine bundles both into a single `bonusArmies` slot, so the card
 * portion is derived as `bonusArmies - eliminatePlayerBonus`. This is exact for
 * the common single-elimination case (one conquering attack eliminates one
 * player); a rare multi-elimination-in-one-interrupt would fold the extra
 * elimination armies into the "card" figure.
 */
export function placementBonusText(
  state: { config: { eliminatePlayerBonus: number }; bonusArmies?: Record<string, number> },
  myPlayerId: string,
  phase: string,
): string | null {
  const total = state.bonusArmies?.[myPlayerId] ?? 0

  if (phase === 'post-elim-placement') {
    const elim = state.config.eliminatePlayerBonus
    const card = Math.max(0, total - elim)
    const parts = [`+${elim} elimination bonus`]
    if (card > 0) parts.push(`+${card} card bonus`)
    return parts.join(', ')
  }

  // Any bonus armies in a normal placement come from a card turn-in.
  return total > 0 ? `+${total} card bonus` : null
}
